/**
 * useLifecycle — reactive view of the family's terms-version state, plus
 * the imperative action to accept the current version. The terms-version
 * source of truth is /config/public.terms_version vs billing.terms_version.
 *
 * (Family-deletion state lives in ``../erasure/useErasure`` — separate
 * surface, separate hook.)
 */

import { useEffect, useState } from "react";
import { API_BASE } from "../api";
import { useBilling } from "../billing/useBilling";
import { acceptTerms } from "./api";

export interface VerticalRolloutFlags {
  game_enabled: boolean;
  animation_enabled: boolean;
  board_enabled: boolean;
}

/** Feature-level rollout flags. Independent of vertical gates — a feature
 * can be off while its host vertical is on. Older backends without this bag
 * are treated as "everything off" (safe default). */
export interface FeatureFlags {
  animation_editor: boolean;
  /** Lands & Residents: the map of lands, residents, idea cards, wake-ups. */
  lands?: boolean;
}

interface PublicConfig {
  terms_version: number;
  social_layer_enabled: boolean;
  trial_days: number;
  /** Bug #22: which non-freeform verticals the chrome should expose. Older
   * backends without this field are treated as "everything hidden" — the
   * safe default while the merged wave is stabilizing. */
  verticals?: VerticalRolloutFlags;
  /** Feature-level rollout flags. See ``FeatureFlags``. */
  features?: FeatureFlags;
}

let _publicConfig: PublicConfig | null = null;
const _configListeners = new Set<() => void>();

/** Run ``fn`` every time the public config is (re)loaded. For module-level
 * caches that derive from the flags — ``chrome/verticals`` subscribes at
 * import. Inverted on purpose: this module importing ``verticals`` back would
 * be a cycle that hits ``_publicConfig``'s TDZ when ``verticals`` evaluates
 * first. A subscriber that loads after the config has landed reads it
 * directly, so it needs no replay. */
export function onPublicConfigLoaded(fn: () => void): () => void {
  _configListeners.add(fn);
  return () => {
    _configListeners.delete(fn);
  };
}

async function _loadPublicConfig(): Promise<PublicConfig> {
  if (_publicConfig) return _publicConfig;
  // MUST be absolute. A bare "/config/public" resolves against the FRONTEND
  // origin, which in production is the Amplify/S3 bucket — it has no /config
  // route, so the SPA fallback answers with index.html, `.json()` throws on
  // "<", the caller's .catch() swallows it, and every flag silently falls back
  // to the all-off default. That shipped the "Coming soon!" picker to prod
  // with the backend flags on (verified 2026-07-23). Dev never caught it
  // because vite.config.ts proxies /config to localhost:8000.
  // Unauthenticated endpoint — "same-origin" credentials means nothing is sent
  // cross-origin, which is what we want; CORS_ORIGINS already allows worldby.me.
  const url = `${API_BASE}/config/public`;
  const resp = await fetch(url, { credentials: "same-origin" });
  if (!resp.ok) {
    throw new Error(`config/public: HTTP ${resp.status} from ${url}`);
  }
  // Fail LOUD on a non-JSON body rather than letting `.json()` throw an opaque
  // SyntaxError — the misrouted-to-SPA case is the one we most need named.
  const contentType = resp.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    throw new Error(
      `config/public: expected JSON from ${url}, got "${contentType}". ` +
        "This usually means the request hit the frontend origin's SPA " +
        "fallback instead of the backend — check VITE_API_URL.",
    );
  }
  const data: PublicConfig = await resp.json();
  _publicConfig = data;
  // The chrome verticals list (bug #22) refreshes through this too — it
  // subscribes via onPublicConfigLoaded, so this module never imports it back.
  _configListeners.forEach((fn) => fn());
  return data;
}

const DEFAULT_VERTICAL_FLAGS: VerticalRolloutFlags = {
  game_enabled: false,
  animation_enabled: false,
  board_enabled: false,
};

const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  animation_editor: false,
  lands: false,
};

/** Synchronous access to the rollout flags. Returns the "all hidden" default
 * until /config/public has resolved. Callers that want to react to the load
 * should use `useLifecycle`'s underlying state (or wire up their own hook). */
export function verticalRolloutFlags(): VerticalRolloutFlags {
  return _publicConfig?.verticals ?? DEFAULT_VERTICAL_FLAGS;
}

/** Synchronous access to feature flags. "Everything off" until config
 * loads — matches the caller convention we use for verticals. */
export function featureFlags(): FeatureFlags {
  return _publicConfig?.features ?? DEFAULT_FEATURE_FLAGS;
}

/** Reactive React hook wrapping ``featureFlags()``. Re-renders when
 * /config/public resolves (and again on any future refresh). Prefer this
 * to `featureFlags()` inside React trees so the UI unlocks a feature the
 * moment the config lands, without a full remount. */
export function useFeatureFlags(): FeatureFlags {
  const [flags, setFlags] = useState<FeatureFlags>(featureFlags());
  useEffect(() => {
    // Ensure the fetch is in flight — App-level effect fires this too,
    // but this hook may mount before that on isolated test pages.
    void ensurePublicConfigLoaded();
    const fn = () => setFlags(featureFlags());
    _configListeners.add(fn);
    return () => {
      _configListeners.delete(fn);
    };
  }, []);
  return flags;
}

/** Ensure the config has been fetched at least once. Idempotent.
 *
 * Still resolves on failure — the safe-default flags are a legitimate
 * degraded mode and a config blip must not white-screen the app. But the
 * failure is now LOGGED: swallowing it silently is what let the misrouted
 * fetch above ship "Coming soon!" to production for weeks with the backend
 * flags on. If you see this in the console, the picker + terms-version gate
 * are running on defaults, not on real config. */
export function ensurePublicConfigLoaded(): Promise<void> {
  return _loadPublicConfig()
    .then(() => undefined)
    .catch((err) => {
      console.error(
        "[lifecycle] /config/public failed to load — running on safe-default " +
          "flags (all verticals hidden, terms gate inert).",
        err,
      );
      return undefined;
    });
}

/** TEST-ONLY: seed the module's config cache directly. Never call this from
 * production code — production reads /config/public through the normal
 * ``_loadPublicConfig`` path. Exists so wave-era vitest suites can assert
 * game/animation/board picker behaviour without wiring the real network
 * fetch; bug #22 gated those verticals off by default. Callers must call
 * ``refreshVerticalsFromConfig()`` from ``../chrome/verticals`` after this
 * to propagate the flags to the exported picker lists. */
export function __setPublicConfigForTest(config: Partial<PublicConfig> | null): void {
  _publicConfig = config
    ? {
        terms_version: 1,
        social_layer_enabled: true,
        trial_days: 0,
        ...config,
      }
    : null;
  _configListeners.forEach((fn) => fn());
}

export interface UseLifecycleValue {
  /** Current platform terms version. */
  currentTermsVersion: number | null;
  /** Tenant's last-accepted terms version (from billing). */
  acceptedTermsVersion: number | null;
  /** True iff a reaccept is required. */
  needsReaccept: boolean;
  acceptCurrent: () => Promise<void>;
}

export function useLifecycle(): UseLifecycleValue {
  const b = useBilling();
  const [config, setConfig] = useState<PublicConfig | null>(_publicConfig);

  useEffect(() => {
    if (_publicConfig === null) {
      // Via ensurePublicConfigLoaded (which catches + logs) rather than the
      // raw loader: _loadPublicConfig now throws on a non-OK / non-JSON
      // response, and a bare `.then(setConfig)` would surface that as an
      // UNHANDLED rejection (a real network blip in prod; the 404 fetch stub
      // in tests). On failure _publicConfig stays null, so setConfig is a
      // no-op — same safe-default state as before.
      void ensurePublicConfigLoaded().then(() => setConfig(_publicConfig));
    }
    const fn = () => setConfig(_publicConfig);
    _configListeners.add(fn);
    return () => {
      _configListeners.delete(fn);
    };
  }, []);

  const currentTermsVersion = config?.terms_version ?? null;
  const acceptedTermsVersion = b.terms_version ?? null;
  const needsReaccept =
    currentTermsVersion !== null
    && acceptedTermsVersion !== null
    && currentTermsVersion > acceptedTermsVersion;

  return {
    currentTermsVersion,
    acceptedTermsVersion,
    needsReaccept,
    acceptCurrent: async () => {
      if (currentTermsVersion !== null) {
        await acceptTerms(currentTermsVersion);
        await b.refresh();
      }
    },
  };
}
